import type { Role } from "@compass/prisma";

/**
 * PRTL-1334 — Compass User / Compass Super User are internal-staff roles and
 * may only be granted to Compass-domain email addresses. Client-firm users
 * wrongly holding these roles caused a cross-org document-upload notification
 * leak; this allowlist is the guard that prevents recurrence.
 *
 * Shared between the admin panel (server actions + bulk import) and the API
 * server (`users.adminUpdate`) so every write path enforces the same rule from
 * one source of truth.
 */
export const COMPASS_EMAIL_DOMAINS = ["compasscfosolutions.com", "herocfo.com"] as const;

// Type-only import of `Role` above, plus `satisfies`, keeps these literals checked
// against the Prisma enum without a runtime import of ./user — that module's
// transitive graph (tiptap, posts) does not resolve under Turbopack and would be
// pulled into the admin client bundle.
const COMPASS_ROLES = ["COMPASS_USER", "COMPASS_SUPERUSER"] as const satisfies readonly Role[];

export const COMPASS_ROLE_EMAIL_ERROR =
    "The Compass User and Compass Super User roles require a @compasscfosolutions.com or @herocfo.com email address.";

export function isCompassRole(role: string | null | undefined): boolean {
    return role != null && (COMPASS_ROLES as readonly string[]).includes(role);
}

export function emailHasCompassDomain(email: string | null | undefined): boolean {
    if (!email) return false;
    const domain = email.split("@").pop()?.toLowerCase().trim();
    return domain !== undefined && (COMPASS_EMAIL_DOMAINS as readonly string[]).includes(domain);
}

/**
 * Returns true when the (role, email) pair is permitted. A non-Compass role is
 * always allowed. When `email` is absent — e.g. a role-only edit submitted
 * through a partial update schema — this defers to the caller, which resolves
 * the effective email from the persisted user before writing.
 */
export function isCompassRoleEmailAllowed(
    role: string | null | undefined,
    email: string | null | undefined,
): boolean {
    if (!isCompassRole(role)) return true;
    if (email === undefined || email === null) return true;
    return emailHasCompassDomain(email);
}
